import type { Node, Plan } from "@melo/shared";
import { store, type Tables } from "./store";
import { byRank, isDone, statusOf, todayIso } from "./select";
import { can, roleIn } from "./access";

/**
 * What is waiting on the signed-in person: questions they can answer, work they review, and agent
 * plans they can approve. Home lists it and the sidebar counts it, from this one definition.
 */
export function waitingOnMe(t: Tables): { questions: Node[]; reviews: Node[]; plans: Plan[]; count: number } {
  const me = store.me?.id;
  const nodes = [...t.nodes.values()].filter((n) => !n.draft);
  const questions = nodes.filter((n) => n.blocker && n.blocker.actorId !== me && can(n.spaceId, "member")).sort((a, b) => (a.blocker!.at < b.blocker!.at ? 1 : -1));
  const reviews = nodes
    .filter((n) => {
      if (statusOf(t.kinds.get(n.kindId), n.status)?.category !== "review") return false;
      const reviewer = n.assignees.find((a) => a.role === "reviewer");
      // A named reviewer owns the review; otherwise it falls to the project's producers.
      return reviewer ? reviewer.actorId === me : roleIn(n.spaceId) === "producer";
    })
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const plans = [...t.plans.values()].filter((p) => p.status === "open" && can(p.spaceId, "member")).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return { questions, reviews, plans, count: questions.length + reviews.length + plans.length };
}

export type WorkGroup = { key: string; label: string; items: Node[] };

/** The signed-in person's open work, grouped the way a day is planned: late, this week, later, undated. */
export function myWork(t: Tables): WorkGroup[] {
  const me = store.me?.id;
  const today = todayIso();
  const end = new Date(`${today}T00:00:00`);
  end.setDate(end.getDate() + 7);
  const week = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`;
  const mine = [...t.nodes.values()].filter((n) => !n.draft && !isDone(t, n) && t.kinds.get(n.kindId)?.key !== "phase" && n.assignees.some((a) => a.actorId === me && a.role === "owner"));
  const by = (a: Node, b: Node) => (a.dueDate ?? "9999").localeCompare(b.dueDate ?? "9999") || byRank(a, b);
  const groups: WorkGroup[] = [
    { key: "late", label: "Overdue", items: mine.filter((n) => n.dueDate && n.dueDate < today).sort(by) },
    { key: "week", label: "Due in the next 7 days", items: mine.filter((n) => n.dueDate && n.dueDate >= today && n.dueDate <= week).sort(by) },
    { key: "later", label: "Later", items: mine.filter((n) => n.dueDate && n.dueDate > week).sort(by) },
    { key: "none", label: "No due date", items: mine.filter((n) => !n.dueDate).sort(by) },
  ];
  return groups.filter((g) => g.items.length);
}
